// Split a generated rubric into the opening lines and one block per question,
// then put rewritten blocks back in the same places.

// Q.1.i - …, Q.14 Question: …, and a heading that is only ### Q.14.
const HEADER_RE = /^[ \t]*(?:#{1,6}[ \t]*)?(?:\*\*)?Q\.?\s*([0-9]+(?:\.[a-zA-Z0-9]+)*)(?:\*\*)?(?:[ \t]*$|\s*[-–—:]|\s*(?:\*\*)?Question\b)/gim;

export function splitRubricQuestions(markdown) {
  const text = String(markdown || '').replace(/\r\n/g, '\n');
  const headers = [];
  HEADER_RE.lastIndex = 0;
  let m;
  while ((m = HEADER_RE.exec(text)) !== null) {
    const lineEnd = text.indexOf('\n', m.index);
    const line = text.slice(m.index, lineEnd === -1 ? text.length : lineEnd);
    headers.push({
      id: m[1],
      display: `Q.${m[1]}`,
      start: m.index
    });
  }
  if (!headers.length) return { preamble: text, blocks: [] };
  const starts = headers.map((h) => parentLineStart(text, h.start));
  const preamble = text.slice(0, starts[0]).replace(/\s+$/, '');
  const blocks = headers.map((h, i) => {
    const end = i + 1 < starts.length ? starts[i + 1] : text.length;
    return { ...h, text: text.slice(starts[i], end).replace(/\s+$/, '') };
  });
  return { preamble, blocks };
}

// A choice line sitting directly above Q.1.i belongs to that part, not to the previous question.
function parentLineStart(text, headerStart) {
  const before = text.slice(0, headerStart).replace(/\s+$/, '');
  const tail = before.match(/(?:^|\n)([ \t]*Q\.?\s*\d+\b(?!\s*\.)[^\n]*\bany\s+\d+\b[^\n]*)$/i);
  if (!tail || !parseChoiceParent(tail[1])) return headerStart;
  const at = before.lastIndexOf(tail[1]);
  return at === -1 ? headerStart : at;
}

const QUESTION_MARKS_RE = /^[ \t]*(?:#{1,6}[ \t]*)?(?:\*\*)?Q\.?\s*([0-9]+(?:\.[A-Za-z0-9]+)*)\s*[-–—:].+?\(\s*([0-9]+(?:\.[0-9]+)?)\s*marks?\s*\)\s*$/gim;

function paperUsesChoice(text) {
  return /attempt any\s+\d+/i.test(text)
    || /any\s+\d+\s+(?:out of|of)\s+\d+/i.test(text)
    || /best\s+\d+\s+of/i.test(text)
    || /کوئی/.test(text);
}

// "Q.1 Write short answers to any 3 questions. (each question carries 2 marks)"
// Q.1.i is a part, not this parent. "Attempt any 1 of 2" with no question number is not one either.
export function parseChoiceParent(line) {
  const raw = String(line || '').replace(/\*\*/g, '').trim();
  const idMatch = raw.match(/^(?:#{1,6}\s*)?Q\.?\s*(\d+)\b(?!\s*\.)/i);
  if (!idMatch || !/\bany\s+\d+\b/i.test(raw)) return null;
  if (!/\b(?:questions?|attempt|each|carries)\b/i.test(raw)) return null;
  const eachMatch = raw.match(/\beach\b[^.\n]{0,60}?([0-9]+(?:\.[0-9]+)?)\s*marks?\b/i)
    || raw.match(/\bcarries\s+([0-9]+(?:\.[0-9]+)?)\s*marks?\b/i)
    || raw.match(/([0-9]+(?:\.[0-9]+)?)\s*marks?\s+each\b/i);
  return {
    id: idMatch[1],
    count: Number(raw.match(/\bany\s+(\d+)\b/i)[1]),
    each: eachMatch ? Number(eachMatch[1]) : null,
    line: raw
  };
}

function questionEntries(text) {
  const out = [];
  QUESTION_MARKS_RE.lastIndex = 0;
  let mark;
  while ((mark = QUESTION_MARKS_RE.exec(text)) !== null) {
    out.push({ id: mark[1], marks: Number(mark[2]) });
  }
  return out;
}

function choiceParents(text) {
  const out = [];
  String(text || '').split(/\n/).forEach((line) => {
    const parent = parseChoiceParent(line);
    if (parent) out.push(parent);
  });
  return out;
}

// Attempt any K of parts worth M is K × M. Parts under that parent are not added again.
// Returns null when no such parent can be priced, so a vague "Attempt any 1 of 2" keeps its printed total.
function obtainableTotal(text) {
  const parents = choiceParents(text);
  if (!parents.length) return null;
  const questions = questionEntries(text);
  const covered = new Set();
  let totalCents = 0;
  let used = false;
  parents.forEach((parent) => {
    const kids = questions.filter((q) => q.id.startsWith(`${parent.id}.`) && !covered.has(q.id));
    let each = parent.each;
    if (each == null && kids.length && kids.every((kid) => kid.marks === kids[0].marks)) each = kids[0].marks;
    if (!kids.length || each == null || !parent.count) return;
    covered.add(parent.id);
    kids.forEach((kid) => covered.add(kid.id));
    totalCents += Math.round(parent.count * each * 100);
    used = true;
  });
  if (!used) return null;
  questions.forEach((q) => {
    if (!covered.has(q.id)) totalCents += Math.round(q.marks * 100);
  });
  return totalCents / 100;
}

function replaceTotal(text, total) {
  const printed = Number.isInteger(total) ? String(total) : String(total);
  return text.replace(/^([ \t]*Total Marks\s*:\s*)([0-9]+(?:\.[0-9]+)?)/im, (full, prefix, num) => (
    Math.abs(Number(num) - total) < 0.001 ? full : prefix + printed
  ));
}

function rubricHasParent(text, id) {
  return choiceParents(text).some((parent) => parent.id === id);
}

// Put the teacher's "any K" line back above Q.N.i when generation dropped it.
export function restoreChoiceParents(rubric, source) {
  const parents = choiceParents(source);
  if (!parents.length) return String(rubric || '');
  let text = String(rubric || '');
  parents.forEach((parent) => {
    if (rubricHasParent(text, parent.id)) return;
    const childRe = new RegExp(`^[ \\t]*(?:#{1,6}[ \\t]*)?(?:\\*\\*)?Q\\.?\\s*${parent.id}\\.`, 'im');
    const child = childRe.exec(text);
    if (!child) return;
    text = `${text.slice(0, child.index)}${parent.line}\n\n${text.slice(child.index)}`;
  });
  return text;
}

/** Total Marks is the sum of the question marks, unless the paper uses a choice rule. */
export function alignRubricPaperTotal(markdown) {
  const text = String(markdown || '');
  const choiceTotal = obtainableTotal(text);
  if (choiceTotal != null) return replaceTotal(text, choiceTotal);
  if (paperUsesChoice(text)) return text;
  const values = questionEntries(text).map((entry) => entry.marks);
  if (!values.length) {
    const statementRe = /^[ \t]*(?:#{1,6}[ \t]*)?(?:\*\*)?Question\s*:\s*.+?\(\s*([0-9]+(?:\.[0-9]+)?)\s*marks?\s*\)/gim;
    let mark;
    while ((mark = statementRe.exec(text)) !== null) values.push(Number(mark[1]));
  }
  if (!values.length) return text;
  const cents = values.reduce((sum, n) => sum + Math.round(n * 100), 0);
  return replaceTotal(text, cents / 100);
}

export function replaceRubricBlockAt(markdown, index, replacement) {
  const { preamble, blocks } = splitRubricQuestions(markdown);
  if (index < 0 || index >= blocks.length) return String(markdown || '');
  const next = blocks.map((block, i) => (
    i === index && String(replacement || '').trim() ? String(replacement).trim() : block.text
  ));
  return [preamble, ...next].filter((part) => part && String(part).trim()).join('\n\n') + '\n';
}

export function replaceRubricBlocks(markdown, replacements) {
  const { preamble, blocks } = splitRubricQuestions(markdown);
  if (!blocks.length) return String(markdown || '');
  const next = blocks.map((block) => {
    const repl = replacements && replacements.get(block.id);
    return repl && String(repl).trim() ? String(repl).trim() : block.text;
  });
  return [preamble, ...next].filter((part) => part && String(part).trim()).join('\n\n') + '\n';
}
